import { FC, useCallback, useMemo } from "react";
import {
  AxisLabelsFormatterContextObject,
  ChartSelectionCallbackFunction,
  Point,
} from "highcharts";
import moment from "moment/moment";
import { Box } from "@mui/material";
import { generateBillingExplorerChartTooltipMarkup } from "./utils/helpers/tooltip/generateBillingExplorerChartTooltipMarkup";
import { generateBillingExplorerChartCumulativeTooltipMarkup } from "./utils/helpers/tooltip/generateBillingExplorerChartCumulativeTooltipMarkup";
import { generateBillingExplorerChartBudgetTooltipMarkup } from "./utils/helpers/tooltip/generateBillingExplorerChartBudgetTooltipMarkup";
import { budgetSeriesName } from "../../../../../common/billing-explorer/utils/addBudgetLineIntoBillingExplorerChartData";
import { getHighChartsColumnDataLabels } from "@/storybook/c8r-highcharts/common/utils/helpers/getHighChartsColumnDataLabels";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { resourceExplorerWidgetDataLoadingSelector } from "@/store/resource-explorer/selectors/loading/resourceExplorerWidgetDataLoadingSelector";
import { moneyFormatter } from "@/utils/numeral/moneyFormatter";
import { resourceExplorerChartBaseSelector } from "@/store/resource-explorer/selectors/resourceExplorerChartBaseSelector";
import { MultiTypeHighcharts } from "@/storybook/c8r-highcharts/MultiTypeHighcharts";
import { resourceExplorerChartDataSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/chart/resourceExplorerChartDataSelector";
import { resourceExplorerPercentBasedChartDataSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/chart/resourceExplorerPercentBasedChartDataSelector";
import { resourceExplorerChartLabelsSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/chart/resourceExplorerChartLabelsSelector";
import { formatDate } from "@/utils/helpers/date-time/datetime-format";
import { changeResourceExplorerDateThunk } from "@/store/resource-explorer/thunks/resource-explorer/changeResourceExplorerDateThunk";
import { resourceExplorerChartSettingsSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/chart/settings/resourceExplorerChartSettingsSelector";
import { resourceExplorerChartDataSeriesMapSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/chart/resourceExplorerChartDataSeriesMapSelector";
import { resourceExplorerPercentBasedChartDataSeriesMapSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/chart/resourceExplorerPercentBasedChartDataSeriesMapSelector";
import { resourceExplorerChartDataSeriesTotalCostsMapSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/resourceExplorerChartDataSeriesTotalCostsMapSelector";
import { chartCumulativeSeriesName } from "@/storybook/c8r-highcharts/common/utils/constants/chartDefaultConfigs";
import { TooltipMarkupGenerator } from "@/storybook/c8r-highcharts/common/utils/types/common";
import { billingExplorerChartTypeSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/billingExplorerChartTypeSelector";
import { organisationQuarterStartMonthSelector } from "@/store/common/selectors/org-current-settings/organisationQuarterStartMonthSelector";
import { resourceExplorerDatesSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/data/resourceExplorerDatesSelector";
import { getPieChartTooltipValueFormatter } from "@/storybook/c8r-highcharts/common/utils/helpers/getPieChartTooltipValueFormatter";
import { resourceExplorerWidgetTotalCostSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/resource-explorer-widget/resourceExplorerWidgetTotalCostSelector";

export const BillingExplorerChart: FC = () => {
  const dispatch = useAppDispatch();
  const chartType = useAppSelector(billingExplorerChartTypeSelector);
  const totalCost = useAppSelector(resourceExplorerWidgetTotalCostSelector);

  const chartBase = useAppSelector(resourceExplorerChartBaseSelector);
  const loading = useAppSelector(resourceExplorerWidgetDataLoadingSelector);
  const chartDataCostBased = useAppSelector(resourceExplorerChartDataSelector);

  const chartDataCostBasedMap = useAppSelector(
    resourceExplorerChartDataSeriesMapSelector,
  );
  const chartDataPercentBasedMap = useAppSelector(
    resourceExplorerPercentBasedChartDataSeriesMapSelector,
  );
  const chartDataPercentBased = useAppSelector(
    resourceExplorerPercentBasedChartDataSelector,
  );
  const totalCostsMap = useAppSelector(
    resourceExplorerChartDataSeriesTotalCostsMapSelector,
  );
  const chartLabels = useAppSelector(resourceExplorerChartLabelsSelector);
  const settings = useAppSelector(resourceExplorerChartSettingsSelector);

  const quarterStartMonth = useAppSelector(
    organisationQuarterStartMonthSelector,
  );
  const dates = useAppSelector(resourceExplorerDatesSelector);

  const chartData =
    chartBase === "percent" ? chartDataPercentBased : chartDataCostBased;

  const columnDataLabels = useMemo(
    () => getHighChartsColumnDataLabels(chartType, chartBase),
    [chartBase, chartType],
  );

  const yAxisLabelsFormatter = useCallback(
    (params: AxisLabelsFormatterContextObject) => {
      return chartBase === "cost"
        ? `${moneyFormatter(+params.value)}`
        : `${+params.value}%`;
    },
    [chartBase],
  );

  const tooltipValueFormatter: TooltipMarkupGenerator = useCallback(
    (point: Point) => {
      const total = totalCostsMap?.get(point.index.toString()) ?? 0;

      const isCumulative =
        point.series.type === "line" &&
        point.series.name.startsWith(chartCumulativeSeriesName);

      if (isCumulative) {
        return generateBillingExplorerChartCumulativeTooltipMarkup(
          total,
          point?.y ?? 0,
          chartDataCostBased?.categories?.at(0) ?? "",
          chartDataCostBased?.categories?.at(point.index) ?? "",
        );
      }

      const isBudget =
        point.series.type === "line" &&
        point.series.name.startsWith(budgetSeriesName);

      if (isBudget) {
        return generateBillingExplorerChartBudgetTooltipMarkup(point?.y ?? 0);
      }

      const costBasedData = chartDataCostBasedMap?.get(point.series.name)?.data;
      const percentBasedData = chartDataPercentBasedMap?.get(
        point.series.name,
      )?.data;

      const value = costBasedData?.[point.index]?.y ?? 0;
      const percent = percentBasedData?.[point.index]?.y ?? 0;

      return generateBillingExplorerChartTooltipMarkup(
        `${point.category}`,
        point.series.name,
        `${point.color}`,
        value,
        percent,
        total,
      );
    },
    [
      chartDataCostBasedMap,
      chartDataPercentBasedMap,
      totalCostsMap,
      chartDataCostBased,
    ],
  );

  const pieChartTooltipValueFormatter: TooltipMarkupGenerator = useMemo(
    () => getPieChartTooltipValueFormatter(dates, quarterStartMonth, totalCost),
    [dates, quarterStartMonth, totalCost],
  );

  const zoomHandler: ChartSelectionCallbackFunction = useCallback(
    (event) => {
      if (!event.xAxis) {
        return;
      }

      const minIndex = Math.floor(event.xAxis.at(0)?.min ?? 0);
      const maxIndex = Math.ceil(event.xAxis.at(0)?.max ?? 0);

      const datesFromLabels = Object.values(chartLabels ?? {});

      const start = datesFromLabels.at(minIndex);
      const end = datesFromLabels.at(maxIndex);

      const dateTo = formatDate(moment(end), {
        type: "yearMonthDay",
      });
      const dateFrom = formatDate(moment(start), {
        type: "yearMonthDay",
      });

      if (!dateTo || !dateFrom) {
        return;
      }

      dispatch(
        changeResourceExplorerDateThunk({
          label: null,
          from: dateFrom,
          to: dateTo,
        }),
      );

      return false; // Return false to prevent Highcharts default zoom
    },
    [dispatch, chartLabels],
  );

  return (
    <Box height={370}>
      <MultiTypeHighcharts
        chartData={chartData}
        chartType={chartType}
        onChartZoom={zoomHandler}
        chartScale={settings?.scale}
        showLegends={settings?.show_legends}
        columnDataLabels={columnDataLabels}
        showDataLabels={settings?.show_data_labels}
        percentBased={chartBase === "percent"}
        loading={loading ? loadingProps : false}
        yAxisLabelsFormatter={yAxisLabelsFormatter}
        tooltipMarkupGenerator={
          chartType === "pie"
            ? pieChartTooltipValueFormatter
            : tooltipValueFormatter
        }
        cumulativeLine={settings?.cumulative_line && chartBase === "cost"}
        noDataMessage="No data by your selected date range and filters."
        stackedChartHeadingLabelFormatter={
          chartBase === "cost" ? stackedChartHeadingLabelFormatter : undefined
        }
      />
    </Box>
  );
};

const stackedChartHeadingLabelFormatter = (value: number) =>
  `${moneyFormatter(value) ?? value}`;

const loadingProps = { showLoader: false };
